/* eslint-disable complexity */ /* eslint-disable max-depth */
import { motion } from 'framer-motion'
import { useEffect, useState, useRef, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import { InactiveInscriptionModal } from './InactiveInscriptionModal'
import { InscriptionLoadingModal } from './InscriptionLoadingModal'
import { AddModuleComponent } from './functions'

import { Modal } from '@/components/common/Modal'
import { useDisclosure } from '@/components/nextui'
import { useToast } from '@/context/ToastContext'
import {
    useAuthHook,
    useMinorStatusLoader,
    useStudyModalityLoader
} from '@/hooks'
import { Footer, Header } from '@/layout/initial-inscription'
import { contentTextLabel } from '@/layout/initial-inscription/functions'
import { useAspirantsApi } from '@/services/api/aspirantsApi'
import { useHomologationsApi } from '@/services/api/homologationsApi'
import { useInscriptionsApi } from '@/services/api/inscriptionsApi'
import { useAuthStore } from '@/store/authStore'
import { MessageLevel } from '@/types'
import { ErrorHandlingStrategy } from '@/types/api'
import { Forms } from '@/types/forms/formStatus'
import { InscriptionProcess } from '@/types/inscriptionProcess'
import {
    MEET_US_STEP_NAME,
    onNavigateToMeetUsPage
} from '@/utils/meetUsNavigation'

// eslint-disable-next-line max-lines-per-function
export const InitialInscription = (): JSX.Element => {
    const [currentStep, setCurrentStep] = useState<number>(0)
    // Página de Conozcámonos a abrir al llegar desde otro módulo
    const [meetUsInitialPage, setMeetUsInitialPage] = useState<number>()
    const [selectedProcesses, setSelectedProcesses] = useState<
        InscriptionProcess[]
    >([])
    const navigate = useNavigate()

    const [steps, setSteps] = useState<Forms[]>()
    const [progress, setProgress] = useState<number>(0)
    const { isAgent, idPaymentOrder, updateIdPaymentOrder, inscriptions } =
        useAuthStore()
    const { idAspirant, idInscription } = useAuthHook()
    const { showToast } = useToast()

    const STEP_PREREQUISITES: Record<string, string[]> = {
        'Sube tus documentos': ['Conozcámonos'],
        Entrevista: ['Conozcámonos']
    }

    const findIncompletePrerequisite = (
        targetStepName: string,
        leavingStepIdx: number
    ): number | null => {
        if (!steps) {
            return null
        }
        const prereqs = STEP_PREREQUISITES[targetStepName] ?? []

        for (const prereqName of prereqs) {
            const idx = steps.findIndex((s) => s.formName === prereqName)

            // Si el paso que se está dejando ES el prerrequisito, se considera
            // como recién completado aunque el backend todavía no lo refleje.
            if (idx !== -1 && !steps[idx].completed && idx !== leavingStepIdx) {
                return idx
            }
        }

        return null
    }

    const currentInscription = inscriptions?.find((i) => i.id === idInscription)
    const isInactiveInscription =
        currentInscription && !currentInscription.isActive
    const hasAnyActiveInscription = (inscriptions ?? []).some((i) => i.isActive)
    const shouldShowInactiveInscriptionModal =
        Boolean(isInactiveInscription) && !hasAnyActiveInscription

    // Cargar studyModality automáticamente cuando el usuario inicia sesión
    useStudyModalityLoader()
    // Precargar isMinor para evitar demoras de render
    useMinorStatusLoader()
    const aspirantsApi = useAspirantsApi({
        messageLevel: MessageLevel.ERRORS,
        errorHandling: ErrorHandlingStrategy.RETURN
    })
    const inscriptionsApi = useInscriptionsApi({
        messageLevel: MessageLevel.ERRORS_AND_INFO_ONLY,
        errorHandling: ErrorHandlingStrategy.RETURN
    })
    const homologationsApi = useHomologationsApi({
        messageLevel: MessageLevel.ERRORS_AND_INFO_ONLY,
        errorHandling: ErrorHandlingStrategy.RETURN
    })

    const {
        isOpen: isOpenSteps,
        onClose: onCloseSteps,
        onOpen: onOpenSteps
    } = useDisclosure({
        defaultOpen: true
    })
    const {
        isOpen: isOpenProgress,
        onClose: onCloseProgress,
        onOpen: onOpenProgress
    } = useDisclosure()

    const [isPolling, setIsPolling] = useState<boolean>(false)
    const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null)
    const pollingAttemptsRef = useRef<number>(0)
    const maxPollingAttempts = 5 // Poll 5 times
    const pollingInterval = 3000 // 3 seconds between attempts

    const [loadingInscription, setLoadingInscription] = useState(false)
    const [isInactiveModalDismissed, setIsInactiveModalDismissed] =
        useState(false)

    const updateForms = async (idAspirant: number): Promise<void> => {
        const idForm = 9
        const { data, status } = await aspirantsApi.getStatusForms(
            idAspirant,
            idInscription
        )

        let isTestCompleted = false

        if (idInscription) {
            const { data: admissionTestData, status: admissionTestStatus } =
                await aspirantsApi.getAdmissionTestRegister(
                    idAspirant,
                    idInscription,
                    idForm
                )

            // Check if test is completed (has responses)
            if (
                admissionTestStatus === 200 &&
                admissionTestData &&
                admissionTestData.length > 0
            ) {
                isTestCompleted = true
            }
        }
        console.log('isTestCompleted', isTestCompleted)

        if (status === 200) {
            console.log('API Progress:', data.progress)
            console.log(
                'inscriptionStatusId from API:',
                data.inscriptionStatusId
            )
            setProgress(data.progress)

            // Update inscription status in store if available
            if (data.inscriptionStatusId) {
                updateInscriptionStatus(data.inscriptionStatusId)
            }

            let filteredForms = data.forms

            const hasNoInscription = data.forms.some(
                (form) => form.message === 'Sin inscripción'
            )
            const notRequiredInterview = data.forms.some(
                (form) =>
                    form.message === 'NOT_REQUIRED' ||
                    form.message === 'UNASSESSED'
            )

            console.log('11notRequiredInterview', notRequiredInterview)
            if (hasNoInscription) {
                filteredForms = filteredForms.filter(
                    (form) =>
                        form.formName !== 'Cuéntanos sobre ti' &&
                        form.formName !== 'Entrevista' &&
                        form.formName !== 'Sube tus documentos' &&
                        form.formName !== 'Orden de Matrícula'
                )
            }

            console.log('11notRequiredInterview', notRequiredInterview)
            if (notRequiredInterview) {
                filteredForms = filteredForms.filter(
                    (form) => form.formName !== 'Entrevista'
                )
            }

            // NEW: Mark admission test as completed instead of filtering
            if (isTestCompleted) {
                filteredForms = filteredForms.map((form) => {
                    if (form.formName === 'Cuéntanos sobre ti') {
                        return {
                            ...form,
                            completed: true,
                            message: 'Test completado'
                        }
                    }

                    return form
                })
            }

            console.log('11filteredForms', filteredForms)

            //Orden de pago
            let hasPaymentOrder = idPaymentOrder !== null && idPaymentOrder > 0

            if (!hasPaymentOrder && idInscription && data.progress === 92) {
                const { data: paymentData, status } =
                    await inscriptionsApi.getOrderPayment(idInscription)

                if (status === 200) {
                    updateIdPaymentOrder(paymentData.id) // Actualizar el id de la orden de pago
                    hasPaymentOrder = true

                    try {
                        const admissionResponse =
                            await aspirantsApi.getAcademicAdmission(
                                idAspirant,
                                idInscription
                            )

                        if (
                            admissionResponse.status === 200 &&
                            admissionResponse.data
                        ) {
                            await aspirantsApi.sendAdmissionSuccessEmail(
                                idInscription,
                                Number(admissionResponse.data.admissionType)
                            )
                        }
                    } catch (error) {
                        console.error('Error sending success email', error)
                    }
                }
            }

            // Mostrar módulo de orden cuando el progreso esté en 90% o más
            const shouldShowOrderModule = hasPaymentOrder

            // Filtrar formularios basado en condiciones
            filteredForms = filteredForms.filter((form) => {
                // Filtrar el formulario 'Pago' hasta que todos los pasos estén completados
                if (form.formName === 'Pago') {
                    // Verificar que todos los pasos estén completados (excluyendo el mismo módulo de Pago)
                    const allStepsCompleted = filteredForms
                        .filter((f) => f.formName !== 'Pago')
                        .every((f) => f.completed)

                    return allStepsCompleted
                }

                return true
            })

            filteredForms.forEach((form: Forms) => AddModuleComponent(form))

            try {
                const basicDataResponse =
                    await aspirantsApi.getBasicData(idAspirant)
                const documentNumber =
                    (basicDataResponse.data as any)?.documentNumber || null

                if (documentNumber) {
                    const projectionResponse =
                        await homologationsApi.getFinalActByDocument(
                            String(documentNumber),
                            idInscription ? Number(idInscription) : undefined
                        )

                    if (
                        projectionResponse.status === 200 &&
                        projectionResponse.data
                    ) {
                        filteredForms = filteredForms.map((form) =>
                            form.formName === 'Proyección'
                                ? { ...form, completed: true }
                                : form
                        )
                    }
                }
            } catch {}
            // ← Quitar la llamada a addEnrollmentOrderIfNeeded

            if (isAgent) {
                filteredForms = filteredForms.filter(
                    (step) =>
                        step.formName === 'Conozcámonos' ||
                        step.formName === 'Sube tus documentos'
                )
            }
            setSteps(filteredForms)
        }
    }

    // NEW: Polling function to check form status after admission test completion
    const startPollingAfterTestCompletion = async (
        idAspirant: number
    ): Promise<void> => {
        setIsPolling(true)
        pollingAttemptsRef.current = 0

        const pollFormStatus = async (): Promise<void> => {
            if (pollingAttemptsRef.current >= maxPollingAttempts) {
                console.log('Max polling attempts reached, stopping polling')
                setIsPolling(false)

                return
            }

            pollingAttemptsRef.current++
            console.log(
                `Polling attempt ${pollingAttemptsRef.current}/${maxPollingAttempts}`
            )

            try {
                await updateForms(idAspirant)

                // Check if the backend process has completed by looking for new steps
                // or changes in the form status
                const currentSteps = steps

                await updateForms(idAspirant)

                // If steps have changed (new interview step appeared, etc.), stop polling
                if (JSON.stringify(currentSteps) !== JSON.stringify(steps)) {
                    console.log('Form status changed, stopping polling')
                    setIsPolling(false)

                    return
                }

                // Schedule next poll
                pollingTimeoutRef.current = setTimeout(
                    pollFormStatus,
                    pollingInterval
                )
            } catch (error) {
                console.error('Error during polling:', error)
                setIsPolling(false)
            }
        }

        // Start polling after initial delay
        pollingTimeoutRef.current = setTimeout(pollFormStatus, pollingInterval)
    }

    // Cleanup polling on unmount
    useEffect(() => {
        return () => {
            if (pollingTimeoutRef.current) {
                clearTimeout(pollingTimeoutRef.current)
            }
        }
    }, [])

    // Una sola llamada a form-status cuando ambos ids están listos (evita 2 llamadas si idInscription se setea después)
    useEffect(() => {
        if (!idAspirant) {
            return
        }

        if (idInscription === undefined) {
            return
        }

        const fetchForms = async () => {
            try {
                setLoadingInscription(true)
                setCurrentStep(0)
                await updateForms(idAspirant)
            } finally {
                // This correctly turns off the loader that was triggered in HeaderSteps
                setLoadingInscription(false)
            }
        }

        fetchForms()
    }, [idAspirant, idInscription])

    // Links desde otros módulos a una página de Conozcámonos
    useEffect(
        () =>
            onNavigateToMeetUsPage((page) => {
                const idx =
                    steps?.findIndex((s) => s.formName === MEET_US_STEP_NAME) ??
                    -1

                if (idx === -1) {
                    return
                }
                setMeetUsInitialPage(page)
                setCurrentStep(idx)
                scroll(0, 100)
            }),
        [steps]
    )

    // La página inicial solo aplica a la siguiente apertura de Conozcámonos
    useEffect(() => {
        if (steps?.[currentStep]?.formName !== MEET_US_STEP_NAME) {
            setMeetUsInitialPage(undefined)
        }
    }, [currentStep, steps])

    // Open the progress modal when progress reaches 99
    useEffect(() => {
        if (progress === 92) {
            onOpenProgress()
        }
    }, [progress, onOpenProgress])

    // Verificar si el módulo de entrevista está habilitado y no completado
    const hasInterviewModuleIncomplete = useMemo(
        () =>
            steps?.some(
                (form) => form.formName === 'Entrevista' && !form.completed
            ),
        [steps]
    )

    // Abrir el modal cuando se detecte el módulo de entrevista incompleto
    useEffect(() => {
        if (hasInterviewModuleIncomplete && progress !== 92) {
            onOpenSteps()
        }
    }, [hasInterviewModuleIncomplete, progress, onOpenSteps])

    const handleNextStep = (): void => {
        if (!idAspirant || !steps) {
            return
        }

        console.log('Current step:', currentStep)
        console.log('Steps:', steps)
        console.log('Current step name:', steps[currentStep]?.formName)

        // NEW: If we just completed "Cuéntanos sobre ti", start polling
        if (steps[currentStep]?.formName === 'Cuéntanos sobre ti') {
            console.log(
                'Admission test completed, starting polling for backend updates'
            )
            startPollingAfterTestCompletion(idAspirant)
        }

        if (currentStep < steps.length - 1) {
            let nextStep = currentStep + 1

            console.log('Initial next step:', nextStep)
            console.log('Next step name:', steps[nextStep]?.formName)
            console.log('Next step completed:', steps[nextStep]?.completed)

            // Skip "Cuéntanos sobre ti" if it's completed
            while (
                nextStep < steps.length &&
                ((steps[nextStep]?.formName === 'Cuéntanos sobre ti' &&
                    steps[nextStep]?.completed) ||
                    (steps[nextStep]?.formName === 'Entrevista' &&
                        steps[nextStep]?.completed))
            ) {
                console.log(
                    'Skipping completed step:',
                    steps[nextStep]?.formName
                )
                nextStep++
            }

            console.log('Final next step:', nextStep)
            console.log('Final step name:', steps[nextStep]?.formName)

            // If we've reached the end after skipping, go to inscription-control
            if (nextStep >= steps.length) {
                console.log('Reached end, navigating to inscription-control')
                navigate('/inscription-control')

                return
            }

            const prereqIdx = findIncompletePrerequisite(
                steps[nextStep]?.formName,
                currentStep
            )

            if (prereqIdx !== null) {
                setCurrentStep(prereqIdx)
                showToast({
                    type: 'info',
                    title: 'Paso pendiente',
                    message: `Para acceder a "${steps[nextStep].formName}", primero debes completar "${steps[prereqIdx].formName}".`
                })
                scroll(0, 100)

                return
            }

            setCurrentStep(nextStep)
            updateForms(idAspirant)
        } else {
            console.log(
                'Already at last step, navigating to inscription-control'
            )
            navigate('/inscription-control')
        }
        scroll(0, 100)
    }

    const handlePreviousStep = (): void => {
        if (currentStep > 0 && steps) {
            let prevStep = currentStep - 1

            // Skip "Cuéntanos sobre ti" if it's completed
            while (
                prevStep >= 0 &&
                ((steps[prevStep]?.formName === 'Cuéntanos sobre ti' &&
                    steps[prevStep]?.completed) ||
                    (steps[prevStep]?.formName === 'Entrevista' &&
                        steps[prevStep]?.completed))
            ) {
                console.log(
                    'Skipping completed step (backwards):',
                    steps[prevStep]?.formName
                )
                prevStep--
            }

            if (prevStep >= 0) {
                setCurrentStep(prevStep)
            }
        }
        scroll(0, 100)
    }

    const handleProcessSelection = (process: InscriptionProcess): void => {
        if (selectedProcesses.some((p) => p.id === process.id)) {
            setSelectedProcesses(
                selectedProcesses.filter((p) => p.id !== process.id)
            )
        } else if (selectedProcesses.length < 2) {
            setSelectedProcesses([...selectedProcesses, process])
        }
    }

    const renderCurrentStep = (): JSX.Element => {
        if (!steps) {
            return <div></div>
        }

        const CurrentComponent = steps[currentStep].component

        if (!CurrentComponent) {
            return (
                <div className="text-red-500 p-4">
                    Error: Component not found for {steps[currentStep].formName}
                </div>
            )
        }

        // Props comunes para todos los componentes
        const commonProps = {
            selectedProcesses,
            setSelectedProcesses,
            onProcessSelection: handleProcessSelection,
            onNext: handleNextStep,
            onPrevious: handlePreviousStep,
            idLastForm: steps[currentStep].idLastForm,
            completed: steps[currentStep].completed,
            progress: progress,
            updateForms: () => updateForms(idAspirant!),
            hasNextStep: currentStep < steps.length - 1,
            initialPage: meetUsInitialPage
        }

        // Props específicos para EnrollmentOrderModule
        if (
            steps[currentStep].formName === 'Orden de Matrícula' ||
            steps[currentStep].formName === 'Pago'
        ) {
            return (
                <CurrentComponent
                    key={steps[currentStep].formName}
                    {...commonProps}
                    inscriptionId={idInscription}
                />
            )
        }

        return (
            <CurrentComponent
                key={steps[currentStep].formName}
                {...commonProps}
            />
        )
    }

    return (
        <section className="w-full">
            <InactiveInscriptionModal
                isOpen={
                    shouldShowInactiveInscriptionModal &&
                    !loadingInscription &&
                    !isInactiveModalDismissed
                }
                setLoadingInscription={setLoadingInscription}
                onClose={() => setIsInactiveModalDismissed(true)}
            />
            {steps &&
                progress !== 92 &&
                !loadingInscription &&
                !isInactiveInscription && (
                    <Modal
                        isOpen={isOpenSteps}
                        onClose={onCloseSteps}
                        text="currentStepInfo"
                        contentText={contentTextLabel(steps)}
                        buttonLabel="continue"
                        newDescription={progress === 100}
                        showInterviewMessage={hasInterviewModuleIncomplete}
                        showPaymentMessage={contentTextLabel(steps) === 'Pago'}
                    />
                )}
            {progress === 92 &&
                !loadingInscription &&
                !isInactiveInscription && (
                    <Modal
                        isOpen={isOpenProgress}
                        onClose={onCloseProgress}
                        text="completeProgress"
                        newDescription={idPaymentOrder ? true : false}
                    />
                )}
            <Header
                progress={progress}
                steps={steps}
                currentStep={currentStep}
                setCurrentStep={setCurrentStep}
                setLoadingInscription={setLoadingInscription}
                isInactiveInscription={Boolean(isInactiveInscription)}
            />
            <InscriptionLoadingModal isOpen={loadingInscription} />
            {!loadingInscription && (
                <article className="w-full">
                    <div className="w-5/6 mx-auto">
                        <motion.div
                            initial={{ x: -50, opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            transition={{ duration: 2, ease: 'easeOut' }}
                            className="mt-4"
                        >
                            {isPolling && (
                                <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                                    <div className="flex items-center space-x-2">
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                                        <span className="text-blue-800">
                                            Verificando actualizaciones del
                                            proceso...
                                        </span>
                                    </div>
                                </div>
                            )}
                            {renderCurrentStep()}
                        </motion.div>
                    </div>
                    <Footer />
                </article>
            )}
        </section>
    )
}
